import type { OrderLine } from '@/features/orders/api';
import { lineStatusLook } from '@/features/orders/orders';
import { dayOrDash, money } from '@/features/orders/orderRecord';
import {
    formatCommodityCode,
    isCommodityCode,
    normaliseCommodityCode,
} from '@/features/product-settings/helpers';

import type {
    Incoterm,
    OrderShipments,
    Parcel,
    ParcelInput,
    ParcelItem,
    ParcelPlace,
    ParcelRoute,
    ParcelStatus,
    ShippableLine,
} from './api';
import { SHIPPING } from './copy';

const P = SHIPPING.parcel;

/** The four steps of the timeline, in order: Queued sits before the first. */
const STEPS: readonly ParcelStatus[] = ['LabelCreated', 'PickedUp', 'InTransit', 'Delivered'];

/** Where a parcel is on the timeline, 0 to 3; -1 while it waits for its label. */
export function stepOf(status: ParcelStatus): number {
    return STEPS.indexOf(status);
}

/** The badge a parcel card carries (order-detail.js parcelBadge): pending and exception before the step. */
export function parcelBadge(
    parcel: Pick<Parcel, 'status' | 'exception'>,
    returning = false,
): {
    label: string;
    variant: 'secondary' | 'success' | 'destructive';
} {
    if (parcel.status === 'Queued') return { label: P.badge.pending, variant: 'destructive' };
    if (parcel.exception) return { label: P.badge.exception, variant: 'destructive' };
    const steps = returning ? SHIPPING.returns.steps : P.steps;
    const label = steps[stepOf(parcel.status)];
    return { label, variant: parcel.status === 'Delivered' ? 'success' : 'secondary' };
}

/**
 * The sentence under the timeline (order-detail.js scanLine): where the parcel is and what to
 * expect, built from what DHL said rather than from fixtures.
 */
export function scanLine(parcel: Parcel): string {
    const S = P.scan;
    const parts: (string | null)[] = [];
    const est = parcel.estimatedDelivery ? S.estimated(dayOrDash(parcel.estimatedDelivery)) : null;
    const signature = parcel.signatureRequired ? S.signature : null;
    const insured = parcel.insured ? S.insured(money(parcel.declaredValue, parcel.currency)) : null;
    const where = parcel.lastScan?.location ?? null;

    switch (parcel.status) {
        case 'LabelCreated':
            parts.push(S.awaitingPickup, est, signature, insured);
            break;
        case 'PickedUp':
            parts.push(S.pickedUp(where), est, signature, insured);
            break;
        case 'InTransit':
            parts.push(S.inTransit(where), est);
            break;
        case 'Delivered':
            parts.push(S.delivered(parcel.signedBy));
            break;
        default:
            break;
    }

    return parts.filter((p): p is string => Boolean(p)).join(' · ');
}

/** The exception's line: DHL's reason and the last scan's place, its details said under it. */
export function exceptionLine(parcel: Parcel): string {
    const exception = parcel.exception;
    if (!exception) return '';
    const where = parcel.lastScan?.location;
    return [exception.text, where ? P.scan.lastScan(where) : null].filter(Boolean).join(' · ');
}

/** DHL's own public tracking page for a number, which Track on DHL opens (ALS-32, 04 Sep). */
export function dhlTracking(number: string): string {
    return `https://www.dhl.com/gb-en/home/tracking.html?tracking-id=${encodeURIComponent(number.replace(/\s+/g, ''))}`;
}

/** "38 × 30 × 12". */
export function dimensions(parcel: Pick<Parcel, 'lengthCm' | 'widthCm' | 'heightCm'>): string {
    return [parcel.lengthCm, parcel.widthCm, parcel.heightCm].map(decimal).join(' × ');
}

const COUNTRIES = new Intl.DisplayNames(['en-GB'], { type: 'region' });

/**
 * DHL's codes outside ISO 3166, named as DHL's reference data names them. The browser knows none
 * of them, or worse, reads XB as the test region "Pseudo-Bidi".
 */
const DHL_ONLY: Readonly<Record<string, string>> = {
    KV: 'Kosovo',
    XB: 'Bonaire',
    XC: 'Curaçao',
    XE: 'Sint Eustatius',
    XM: 'Sint Maarten',
    XN: 'Nevis',
    XS: 'Somaliland',
    XY: 'Saint Barthélemy',
};

/** "Saudi Arabia" for "SA": the rest of DHL's codes are ISO 3166, which the browser can name. */
export function countryName(code: string): string {
    if (DHL_ONLY[code]) return DHL_ONLY[code];
    try {
        return COUNTRIES.of(code) ?? code;
    } catch {
        return code;
    }
}

/** "London, United Kingdom → Riyadh, Saudi Arabia". */
export function routeLine(route: ParcelRoute): string {
    const place = (p: ParcelPlace) => `${p.city}, ${countryName(p.countryCode)}`;
    return P.route(place(route.from), place(route.to));
}

/** "HS 6204.42.00 · Made in Italy": what the commercial invoice declares for one item. */
export function customsLine(
    item: Pick<ParcelItem, 'commodityCode' | 'manufacturerCountry'>,
): string {
    return P.customs(
        formatCommodityCode(item.commodityCode),
        countryName(item.manufacturerCountry),
    );
}

/** "1.4", "0.65": a weight or a side without trailing zeros. */
export function decimal(value: number): string {
    return value.toLocaleString('en-GB', { maximumFractionDigits: 3, useGrouping: false });
}

/** Each packed line's parcel, as the Items tab's Parcel column shows it: P1, P2. */
export function parcelColumn(shipments: OrderShipments | undefined): Map<number, string> {
    const map = new Map<number, string>();
    for (const parcel of shipments?.parcels ?? []) {
        for (const item of parcel.items)
            map.set(item.orderLineId, SHIPPING.parcelColumn(parcel.number));
    }
    return map;
}

/**
 * The builder's note on what it left out: how many, and which statuses, in the order's words
 * (Franka, 25 Sep 2026). Null when nothing is held back.
 */
export function heldBackNote(heldBack: number[], lines: OrderLine[]): string | null {
    if (heldBack.length === 0) return null;
    const statuses: string[] = [];
    for (const id of heldBack) {
        const line = lines.find((l) => l.id === id);
        if (!line) continue;
        const label = lineStatusLook(line.status, line.statusName, line.tone).label;
        if (!statuses.includes(label)) statuses.push(label);
    }
    return SHIPPING.builder.held(heldBack.length, statuses);
}

// ---- the parcel builder ----

/** One commodity line as the builder holds it: text as typed, until it is sent. */
export interface CommodityDraft {
    customsDescription: string;
    commodityCode: string;
    countryId: number | null;
    netWeight: string;
}

export interface ParcelDraft {
    picked: number[];
    lines: Record<number, CommodityDraft>;
    reference: string;
    gross: string;
    dims: string;
    declared: string;
    /** Whether the declared value was typed; until then it follows the picked lines. */
    declaredTouched: boolean;
    incoterm: Incoterm;
    signature: boolean;
    insured: boolean;
    pickup: boolean;
}

/**
 * A new parcel: nothing picked, each shippable line's commodity line filled from the records
 * (the product's name and HS code, the line's origin), and the prototype's switches all on. The
 * weights and the box start empty, with the prototype's values as placeholders: a weight nobody
 * looked at is what DHL would bill.
 */
/**
 * Whether a PDF should open in its own tab rather than in the document viewer: a phone opens it in
 * its own viewer, because Android shows no PDF inside a page.
 */
export function opensPdfInTab(): boolean {
    return window.matchMedia?.('(max-width: 767px), (pointer: coarse)').matches ?? false;
}

/**
 * A document's name as a file name: the card's own text ("Commercial invoice DEV/2026/024-P1.pdf"),
 * with the characters a file name cannot hold made dashes, so the reference's slashes survive as
 * "DEV-2026-024-P1" rather than being cut by the browser (QA, 8 Oct 2026).
 */
export function documentFileName(name: string): string {
    return name.replace(/[\\/:*?"<>|]+/g, '-');
}

export function newParcelDraft(orderNo: string, shipments: OrderShipments): ParcelDraft {
    // The server's next number, not the cards': a voided parcel keeps its number and its
    // reference at DHL, so counting the cards would suggest a reference already used (ALS-350).
    const next = shipments.nextParcelNumber;
    return {
        picked: [],
        lines: Object.fromEntries(
            shipments.shippable.map((l) => [l.orderLineId, commodityFrom(l)]),
        ),
        reference: `${orderNo}-P${next}`,
        gross: '',
        dims: '',
        declared: '',
        declaredTouched: false,
        incoterm: 'DDP',
        signature: true,
        insured: true,
        pickup: true,
    };
}

export function commodityFrom(line: ShippableLine): CommodityDraft {
    return {
        customsDescription: line.customsDescription,
        commodityCode: line.commodityCode ?? '',
        countryId: line.manufacturerCountryId,
        netWeight: '',
    };
}

/** Whether a commodity line still holds exactly what the records gave it (the prototype's Auto chip). */
export function isAuto(draft: CommodityDraft, line: ShippableLine): boolean {
    return (
        draft.customsDescription === line.customsDescription &&
        line.commodityCode !== null &&
        normaliseCommodityCode(draft.commodityCode) === line.commodityCode
    );
}

/** "1,2" and "1.2" are both 1.2; anything that is not a number is null. */
export function parseNumber(text: string): number | null {
    const value = Number(text.trim().replace(',', '.'));
    return text.trim() === '' || !Number.isFinite(value) ? null : value;
}

/** "30 × 24 × 10", "30x24x10" or "30*24*10.5" as three sides; null for anything else. */
export function parseDimensions(text: string): [number, number, number] | null {
    const match =
        /^\s*(\d+(?:[.,]\d+)?)\s*[x×*]\s*(\d+(?:[.,]\d+)?)\s*[x×*]\s*(\d+(?:[.,]\d+)?)\s*$/i.exec(
            text,
        );
    if (!match) return null;
    const sides = [match[1], match[2], match[3]].map((s) => Number(s.replace(',', '.')));
    return sides.every((s) => s > 0) ? (sides as [number, number, number]) : null;
}

/** What the picked lines are worth: the declared value until somebody types one. */
export function linesValue(draft: ParcelDraft, shippable: ShippableLine[]): number {
    return shippable
        .filter((l) => draft.picked.includes(l.orderLineId))
        .reduce((sum, l) => sum + l.unitValue * l.quantity, 0);
}

export type CommodityErrors = Partial<
    Record<'customsDescription' | 'commodityCode' | 'country' | 'netWeight', string>
>;

export interface ParcelErrors {
    reference?: string;
    gross?: string;
    dims?: string;
    declared?: string;
    lines: Record<number, CommodityErrors>;
    /** The banner's list, in the prototype's words: what is missing, by item or field. */
    problems: string[];
}

/** The prototype's checks before DHL is called (order-detail.js, the save handler), plus the country DHL also needs. */
export function validateParcelDraft(draft: ParcelDraft, names: Map<number, string>): ParcelErrors {
    const B = SHIPPING.builder;
    const errors: ParcelErrors = { lines: {}, problems: [] };
    if (draft.picked.length === 0) errors.problems.push(B.problem.pick);

    for (const id of draft.picked) {
        const line = draft.lines[id];
        const bad: CommodityErrors = {};
        if (line.customsDescription.trim().length < 3)
            bad.customsDescription = B.errors.customsDescription;
        if (!line.commodityCode.trim()) bad.commodityCode = B.errors.commodityCode;
        else if (!isCommodityCode(line.commodityCode))
            bad.commodityCode = B.errors.commodityCodeFormat;
        if (line.countryId === null) bad.country = B.errors.country;
        const net = parseNumber(line.netWeight);
        if (net === null || net <= 0) bad.netWeight = B.errors.netWeight;
        if (Object.keys(bad).length > 0) {
            errors.lines[id] = bad;
            errors.problems.push(names.get(id) ?? String(id));
        }
    }

    const gross = parseNumber(draft.gross);
    if (gross === null || gross <= 0) {
        errors.gross = B.errors.gross;
        errors.problems.push(B.problem.gross);
    }

    if (!parseDimensions(draft.dims)) {
        errors.dims = B.errors.dims;
        errors.problems.push(B.problem.dims);
    }

    if (!draft.reference.trim()) {
        errors.reference = B.errors.reference;
        errors.problems.push(B.problem.reference);
    }

    if (draft.declaredTouched) {
        const declared = parseNumber(draft.declared);
        if (declared === null || declared < 0) errors.declared = B.errors.declared;
    }

    return errors;
}

export function hasErrors(errors: ParcelErrors): boolean {
    return errors.problems.length > 0 || errors.declared !== undefined;
}

/** The draft as the API takes it. Call only on a draft that validated. */
export function parcelInput(draft: ParcelDraft): ParcelInput {
    const [lengthCm, widthCm, heightCm] = parseDimensions(draft.dims) ?? [0, 0, 0];
    return {
        reference: draft.reference.trim(),
        grossWeightKg: parseNumber(draft.gross) ?? 0,
        lengthCm,
        widthCm,
        heightCm,
        declaredValue: draft.declaredTouched ? parseNumber(draft.declared) : null,
        incoterm: draft.incoterm,
        signatureRequired: draft.signature,
        insured: draft.insured,
        pickupRequested: draft.pickup,
        lines: draft.picked.map((id) => {
            const line = draft.lines[id];
            return {
                orderLineId: id,
                customsDescription: line.customsDescription.trim(),
                commodityCode: normaliseCommodityCode(line.commodityCode),
                manufacturerCountryId: line.countryId ?? 0,
                netWeightKg: parseNumber(line.netWeight) ?? 0,
            };
        }),
    };
}
